import { ColumnType, Row } from "@asayinc/component-library";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";

import { IClassActionEvent } from "src/store/classActionEvent/types";
import { ClassActionEventsView } from "./ClassActionEvents";
import { CLASS_ACTION_EVENTS_VIEW } from "./ClassActionEvents";

dayjs.extend(relativeTime);
dayjs.extend(utc);

export const UPCOMING_COLUMNS = [
  "issuer",
  "recordDateStart",
  "recordDateEnd",
  "claimsAdministrator",
  "caseDeadline",
  "created",
];

export const ACTIVE_COLUMNS = [
  "issuer",
  "recordDateStart",
  "recordDateEnd",
  "claimsAdministrator",
  "caseDeadline",
  "created",
];

export const RECENTLY_COMPLETED_COLUMNS = [
  "issuer",
  "recordDateStart",
  "recordDateEnd",
  "claimsAdministrator",
  "caseDeadline",
  "created",
];

export const HISTORICAL_COLUMNS = [
  "issuer",
  "recordDateStart",
  "recordDateEnd",
  "claimsAdministrator",
  "caseDeadline",
  "created",
];

export type ColumnDataHelpers = {
  getSort: (sortKey: string) => "asc" | "desc" | undefined;
  onOrderingChange: (data: unknown, sortKey: string) => void;
};

export type UpcomingColumnData = {
  type: ColumnType;
  issuer: ColumnType;
  recordDateStart: ColumnType;
  recordDateEnd: ColumnType;
  claimsAdministrator: ColumnType;
  created: ColumnType;
  caseDeadline: ColumnType;
};

export type ActiveColumnData = {
  type: ColumnType;
  issuer: ColumnType;
  recordDateStart: ColumnType;
  recordDateEnd: ColumnType;
  claimsAdministrator: ColumnType;
  created: ColumnType;
  caseDeadline: ColumnType;
};

export type RecentlyCompletedColumnData = {
  type: ColumnType;
  issuer: ColumnType;
  recordDateStart: ColumnType;
  recordDateEnd: ColumnType;
  claimsAdministrator: ColumnType;
  created: ColumnType;
  caseDeadline: ColumnType;
};

export type HistoricalColumnData = {
  issuer: ColumnType;
  type: ColumnType;
  recordDateStart: ColumnType;
  recordDateEnd: ColumnType;
  claimsAdministrator: ColumnType;
  created: ColumnType;
  caseDeadline: ColumnType;
};

export function getColumns(view: ClassActionEventsView) {
  if (view === CLASS_ACTION_EVENTS_VIEW.UPCOMING) {
    return UPCOMING_COLUMNS;
  } else if (view === CLASS_ACTION_EVENTS_VIEW.ACTIVE) {
    return ACTIVE_COLUMNS;
  } else if (view === CLASS_ACTION_EVENTS_VIEW.RECENTLY_COMPLETED) {
    return RECENTLY_COMPLETED_COLUMNS;
  } else {
    return HISTORICAL_COLUMNS;
  }
}

export function getColumnData(
  view: string,
  { getSort, onOrderingChange }: ColumnDataHelpers
):
  | UpcomingColumnData
  | ActiveColumnData
  | RecentlyCompletedColumnData
  | HistoricalColumnData {
  if (view === CLASS_ACTION_EVENTS_VIEW.UPCOMING) {
    return {
      type: {
        sort: getSort("type"),
        sortFn: (data: unknown) => onOrderingChange(data, "type"),
        style: "text",
        title: "Type",
        tdBodyProps: {
          textProps: {
            weight: 600,
          },
        },
      },
      issuer: {
        sort: getSort("issuer__name"),
        sortFn: (data: unknown) => onOrderingChange(data, "issuer__name"),
        style: "text",
        title: "Issuer",
        tdBodyProps: {
          textProps: {
            weight: 600,
          },
        },
      },
      recordDateStart: {
        sort: getSort("recordDateStart"),
        sortFn: (data: unknown) => onOrderingChange(data, "recordDateStart"),
        style: "text",
        title: "Record Date Start",
      },
      recordDateEnd: {
        sort: getSort("recordDateEnd"),
        sortFn: (data: unknown) => onOrderingChange(data, "recordDateEnd"),
        style: "text",
        title: "Record Date End",
      },
      claimsAdministrator: {
        sort: getSort("claimsAdministrator__name"),
        sortFn: (data: unknown) =>
          onOrderingChange(data, "claimsAdministrator__name"),
        style: "text",
        title: "Claims Administrator",
      },
      created: {
        sort: getSort("created"),
        sortFn: (data: unknown) => onOrderingChange(data, "created"),
        style: "text",
        title: "Created",
      },
      caseDeadline: {
        sort: getSort("caseDeadline"),
        sortFn: (data: unknown) => onOrderingChange(data, "caseDeadline"),
        style: "text",
        title: "Case Deadline",
      },
    };
  } else {
    return {
      type: {
        sort: getSort("type"),
        sortFn: (data: unknown) => onOrderingChange(data, "type"),
        style: "text",
        title: "Type",
        tdBodyProps: {
          textProps: {
            weight: 600,
          },
        },
      },
      issuer: {
        sort: getSort("issuer__name"),
        sortFn: (data: unknown) => onOrderingChange(data, "issuer__name"),
        style: "text",
        title: "Issuer",
        tdBodyProps: {
          textProps: {
            weight: 600,
          },
        },
      },
      recordDateStart: {
        sort: getSort("recordDateStart"),
        sortFn: (data: unknown) => onOrderingChange(data, "recordDateStart"),
        style: "text",
        title: "Record Date Start",
      },
      recordDateEnd: {
        sort: getSort("recordDateEnd"),
        sortFn: (data: unknown) => onOrderingChange(data, "recordDateEnd"),
        style: "text",
        title: "Record Date End",
      },
      claimsAdministrator: {
        sort: getSort("claimsAdministrator__name"),
        sortFn: (data: unknown) =>
          onOrderingChange(data, "claimsAdministrator__name"),
        style: "text",
        title: "Claims Administrator",
      },
      created: {
        sort: getSort("created"),
        sortFn: (data: unknown) => onOrderingChange(data, "created"),
        style: "text",
        title: "Created",
      },
      caseDeadline: {
        sort: getSort("caseDeadline"),
        sortFn: (data: unknown) => onOrderingChange(data, "caseDeadline"),
        style: "text",
        title: "Case Deadline",
      },
    };
  }
}

export function getRowData(
  view: string,
  events: IClassActionEvent[],
  onRowClick: (event: IClassActionEvent) => void
): Row[] {
  const handleRowClick = (data: unknown) => {
    const { event } = data as {
      event: IClassActionEvent;
      idx: string;
    };
    onRowClick(event);
  };

  if (view === CLASS_ACTION_EVENTS_VIEW.UPCOMING) {
    return events.map((event, idx) => {
      const {
        id,
        issuer,
        recordDateStart,
        recordDateEnd,
        claimsAdministrator,
        sourceProviderCaseDeadline,
        created,
      } = event;

      return {
        id,
        data: { event, idx },
        rowData: {
          issuer: {
            content: issuer.name,
            clickHandler: handleRowClick,
          },
          recordDateStart: {
            content: dayjs(recordDateStart).format("MM/DD/YYYY"),
            clickHandler: handleRowClick,
          },
          recordDateEnd: {
            content: dayjs(recordDateEnd).format("MM/DD/YYYY"),
            clickHandler: handleRowClick,
          },
          claimsAdministrator: {
            content: claimsAdministrator.name,
            clickHandler: handleRowClick,
          },
          caseDeadline: {
            content: dayjs(sourceProviderCaseDeadline).format("MM/DD/YYYY"),
            clickHandler: handleRowClick,
          },
          created: {
            content: dayjs(created).format("MM/DD/YYYY"),
            clickHandler: handleRowClick,
          },
        },
      };
    });
  } else {
    return events.map((event, idx) => {
      const {
        id,
        issuer,
        recordDateStart,
        recordDateEnd,
        claimsAdministrator,
        sourceProviderCaseDeadline,
        created,
      } = event;

      return {
        id,
        data: { event, idx },
        rowData: {
          issuer: {
            content: issuer.name,
            clickHandler: handleRowClick,
          },
          recordDateStart: {
            content: dayjs(recordDateStart).format("MM/DD/YYYY"),
            clickHandler: handleRowClick,
          },
          recordDateEnd: {
            content: dayjs(recordDateEnd).format("MM/DD/YYYY"),
            clickHandler: handleRowClick,
          },
          claimsAdministrator: {
            content: claimsAdministrator.name,
            clickHandler: handleRowClick,
          },
          caseDeadline: {
            content: dayjs(sourceProviderCaseDeadline).format("MM/DD/YYYY"),
            clickHandler: handleRowClick,
          },
          created: {
            content: dayjs(created).format("MM/DD/YYYY"),
            clickHandler: handleRowClick,
          },
        },
      };
    });
  }
}
